import { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUp, Mic, Sparkles, History, AlertCircle, CheckCircle2, CornerDownLeft } from 'lucide-react';
import { ConfirmDialog } from '../ui/States';
import { useComposer, type ComposerPlatform } from './useComposer';

const PLACEHOLDER = '¿Qué quieres hacer? Ej. "cambiar horario" o "publicar un aviso"';

/**
 * Composer central compartido entre la home mobile y la desktop companion.
 * Enruta, captura el dato que falte, confirma y ejecuta tareas reales vía el
 * catálogo de intents (Fase 2). La telemetría y la bitácora viven en useComposer.
 */
export default function AssistantComposer({
  platform,
  autoFocus = false,
  controller,
}: {
  platform: ComposerPlatform;
  autoFocus?: boolean;
  /** Permite compartir el estado del composer con las acciones sugeridas del home. */
  controller?: ReturnType<typeof useComposer>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const ownController = useComposer(platform);
  const c = controller ?? ownController;

  const showSuggestions = c.state === 'suggesting' && c.matches.length > 0;
  const showHistory =
    (c.state === 'focused' || c.state === 'idle') && c.history.length > 0 && c.value.trim() === '';
  const collecting = c.state === 'collecting' && !!c.pendingIntent;
  const busy = c.state === 'routing' || c.state === 'executing';

  return (
    <div className="relative">
      <motion.div
        layout
        className={`rounded-2xl border bg-ui-surface transition-colors ${
          c.state === 'idle' ? 'border-ui-border' : 'border-ui-accent/60 shadow-sm'
        }`}
      >
        <div className="flex items-center gap-2 px-3 py-2.5 sm:px-4 sm:py-3">
          <Sparkles size={18} className="shrink-0 text-ui-accent" />
          <input
            ref={inputRef}
            value={c.value}
            autoFocus={autoFocus}
            disabled={busy || collecting}
            onChange={(e) => c.change(e.target.value)}
            onFocus={c.focus}
            onBlur={() => window.setTimeout(c.blur, 120)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') c.submit();
            }}
            placeholder={PLACEHOLDER}
            aria-label="Pídele algo al asistente"
            className="flex-1 min-w-0 bg-transparent text-sm sm:text-base text-ui-text placeholder:text-ui-text-subtle focus:outline-none"
          />
          <button
            type="button"
            title="Dictar (próximamente)"
            aria-label="Dictar por voz, próximamente"
            disabled
            className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-ui-text-subtle/60 cursor-not-allowed"
          >
            <Mic size={17} />
          </button>
          <button
            type="button"
            onClick={c.submit}
            disabled={c.value.trim() === '' || busy}
            aria-label="Enviar"
            className="shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-ui-accent text-ui-text-inverse hover:bg-ui-accent-hover disabled:opacity-40 disabled:hover:bg-ui-accent transition-colors"
          >
            {busy ? (
              <span className="w-4 h-4 border-2 border-ui-text-inverse border-t-transparent rounded-full animate-spin" />
            ) : (
              <ArrowUp size={17} />
            )}
          </button>
        </div>

        <AnimatePresence initial={false}>
          {(showSuggestions || showHistory) && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.18 }}
              className="overflow-hidden border-t border-ui-border"
            >
              <div className="p-2">
                {showHistory && (
                  <p className="px-2 pt-1 pb-1.5 text-xs text-ui-text-subtle">Reciente</p>
                )}
                {showHistory &&
                  c.history.map((item) => (
                    <button
                      key={item}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => c.change(item)}
                      className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-sm text-ui-text-muted hover:bg-ui-text/[0.06] transition-colors"
                    >
                      <History size={15} className="shrink-0 text-ui-text-subtle" />
                      <span className="truncate">{item}</span>
                    </button>
                  ))}

                {showSuggestions &&
                  c.matches.map(({ intent }) => (
                    <button
                      key={intent.id}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => c.runIntent(intent, c.value.trim())}
                      className="w-full flex items-start gap-2.5 px-2 py-2 rounded-lg text-left hover:bg-ui-text/[0.06] transition-colors"
                    >
                      <Sparkles size={15} className="mt-0.5 shrink-0 text-ui-accent" />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-ui-text">{intent.label}</span>
                        <span className="block text-sm text-ui-text-subtle truncate">{intent.description}</span>
                      </span>
                    </button>
                  ))}
              </div>
            </motion.div>
          )}

          {collecting && c.pendingIntent?.execution.param && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.18 }}
              className="overflow-hidden border-t border-ui-border"
            >
              <ParamCapture
                prompt={c.pendingIntent.execution.param.prompt}
                placeholder={c.pendingIntent.execution.param.placeholder}
                onSubmit={c.submitParam}
                onCancel={c.cancel}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {c.feedback && (c.state === 'done' || c.state === 'failed') && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`mt-2 flex items-center gap-2 text-sm ${
              c.state === 'failed' ? 'text-ui-danger' : 'text-ui-success'
            }`}
          >
            {c.state === 'failed' ? <AlertCircle size={15} /> : <CheckCircle2 size={15} />}
            <span>{c.feedback}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <ConfirmDialog
        open={c.state === 'confirming' && !!c.pendingIntent}
        title={c.pendingIntent ? c.pendingIntent.label : ''}
        description={
          c.pendingIntent
            ? `${c.pendingIntent.description} Esta acción se aplicará al confirmar.`
            : undefined
        }
        confirmLabel="Confirmar"
        cancelLabel="Cancelar"
        onConfirm={c.confirm}
        onCancel={c.cancel}
      />
    </div>
  );
}

/** Captura inline de un único dato que un intent necesita para ejecutarse. */
function ParamCapture({
  prompt,
  placeholder,
  onSubmit,
  onCancel,
}: {
  prompt: string;
  placeholder?: string;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState('');
  const ready = draft.trim().length > 0;

  return (
    <div className="p-3">
      <p className="px-1 pb-2 text-sm font-medium text-ui-text">{prompt}</p>
      <div className="flex items-center gap-2">
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && ready) onSubmit(draft.trim());
            if (e.key === 'Escape') onCancel();
          }}
          placeholder={placeholder}
          aria-label={prompt}
          className="flex-1 min-w-0 rounded-lg border border-ui-border bg-ui-bg px-3 py-2 text-sm text-ui-text placeholder:text-ui-text-subtle focus:outline-none focus:border-ui-accent"
        />
        <button
          type="button"
          onClick={() => ready && onSubmit(draft.trim())}
          disabled={!ready}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-ui-accent px-3 py-2 text-sm font-medium text-ui-text-inverse hover:bg-ui-accent-hover disabled:opacity-40 transition-colors"
        >
          <CornerDownLeft size={14} />
          Listo
        </button>
      </div>
      <button
        type="button"
        onClick={onCancel}
        className="mt-2 px-1 text-sm text-ui-text-subtle hover:text-ui-text transition-colors"
      >
        Cancelar
      </button>
    </div>
  );
}
